import { ArrowRight } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageSEO from "../components/PageSEO";
import SiteFooter from "../components/SiteFooter";
import SiteHeader from "../components/SiteHeader";
import { visibleComparisons } from "../content/comparisons";

export default function ComparePage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": "https://anter.ai/compare",
    name: "Anter vs. Every Agent Orchestration Alternative",
    description:
      "Deep-dive comparisons between Anter and specific alternatives — Claude Managed Agents, OpenAI AgentKit, CrewAI, LangGraph, Base44, and building it in-house.",
    url: "https://anter.ai/compare",
    hasPart: visibleComparisons.map((c) => ({
      "@type": "WebPage",
      "@id": `https://anter.ai/compare/${c.slug}`,
      name: c.term,
      url: `https://anter.ai/compare/${c.slug}`,
    })),
  };

  return (
    <>
      <PageSEO
        title="Anter vs. the Alternatives — Claude, OpenAI, CrewAI, LangGraph, Base44"
        description="Compare Anter vs. alternatives (Claude Managed Agents, OpenAI AgentKit, CrewAI, LangGraph, Base44). Deep dive into multi-agent orchestration, pricing, and infrastructure."
        canonical="/compare"
        jsonLd={jsonLd}
      />

      <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text">
        <SiteHeader scrolled />

        <main
          id="main-content"
          tabIndex={-1}
          className="mx-auto w-full max-w-5xl px-4 pb-24 pt-28 sm:px-5 md:px-8 lg:pt-32"
        >
          <Breadcrumbs items={[{ label: "Compare" }]} />

          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
            Comparisons
          </p>
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-text sm:text-5xl">
            Anter vs. the alternatives
          </h1>
          <p className="mb-12 max-w-2xl text-lg leading-relaxed text-text-muted">
            A deep dive per alternative — what a specific vendor's own docs say, and where Anter's
            orchestrator does something different. For the full side-by-side table across every
            vendor at once, see{" "}
            <a
              href="/why-anter"
              className="font-semibold text-primary-text underline-offset-4 hover:underline"
            >
              Why Anter
            </a>
            .
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            {visibleComparisons.map((c) => (
              <a
                key={c.slug}
                href={`/compare/${c.slug}`}
                className="group rounded-2xl border border-border bg-bg-secondary/60 p-5 transition-all duration-200 hover:border-primary-border hover:bg-bg-hover"
              >
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary-text">
                  vs. {c.vendor}
                </p>
                <h2 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-text">
                  {c.term}
                  <ArrowRight className="h-3.5 w-3.5 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                </h2>
                <p className="text-sm leading-relaxed text-text-muted">{c.shortDefinition}</p>
              </a>
            ))}
          </div>

          <section className="mt-16 rounded-2xl border border-primary-border/60 bg-primary-dim/30 p-8">
            <h2 className="mb-2 text-xl font-extrabold tracking-tight text-text">
              See the claims side by side
            </h2>
            <p className="mb-5 max-w-2xl text-sm leading-relaxed text-text-muted">
              Every comparison here is grounded in one vendor's own current documentation, or a
              cited independent review — never asserted across a whole product line.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/why-anter"
                className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-primary-text-strong transition hover:opacity-90"
              >
                Why Anter
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/docs/runtime"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-bg-secondary px-5 py-2.5 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
              >
                Read the runtime docs
                <ArrowRight className="h-4 w-4 text-primary" />
              </a>
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
